import React, { useEffect, useRef } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
);

const getGradient = (ctx, colorStops) => {
  const gradient = ctx.createLinearGradient(0, 0, 0, 400);
  colorStops.forEach((stop) =>
    gradient.addColorStop(stop.position, stop.color),
  );
  return gradient;
};

const LineChart = ({ datasets, labels, options = {} }) => {
  const chartRef = useRef(null);

  useEffect(() => {
    const chart = chartRef.current;

    if (!chart) return;

    const container = chart.canvas.parentNode;
    container.style.position = "relative"; // Ensure position is relative

    const addXAxisSeparators = (chart) => {
      const chartArea = chart.chartArea;
      const xAxis = chart.scales.x;

      if (!xAxis) return;

      const existingSeparators =
        container.querySelectorAll(".x-axis-separator");
      existingSeparators.forEach((sep) => sep.remove());

      const tickPositions = chart.data.labels.map((_, i) =>
        xAxis.getPixelForTick(i),
      );

      const createSeparator = (leftPosition) => {
        const separator = document.createElement("div");
        separator.className = "x-axis-separator";
        separator.style.cssText = `position: absolute; width: 1px; height: 9px; background-color: #B5BBC0; pointer-events: none; z-index: 10; left: ${leftPosition}px; top: ${
          chartArea.bottom + 5
        }px;`;
        container.appendChild(separator);
      };

      // Create separators
      createSeparator(chartArea.left - 26); // Left edge separator
      for (let i = 0; i < tickPositions.length - 1; i++) {
        createSeparator((tickPositions[i] + tickPositions[i + 1]) / 2); // Between tick positions
      }
      createSeparator(chartArea.right + 26); // Right edge separator
    };

    // Add animation configuration
    chart.options.animation = {
      onComplete: () => {
        addXAxisSeparators(chart);
      },
    };

    // Force update the chart to ensure the animation callback is registered
    chart.update();
  }, [datasets, labels]);

  const chartData = {
    labels,
    datasets, // ✅ Use the dataset as-is, since backgroundColor is already a function
  };

  const defaultOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
    },
    layout: {
      padding: { top: 0, bottom: 0 },
    },
    scales: {
      x: {
        ticks: {
          color: "#B5BBC0",
          font: { family: "Poppins", weight: 600, size: 16, lineHeight: 1.5 },
          padding: 0,
          align: "center",
          callback: function (value, index) {
            return this.chart.data.labels[index];
          },
        },
        grid: { display: false },
        border: { color: "#2B3C4A", width: 1 },
      },
      y: {
        ticks: {
          color: "#B5BBC0",
          font: { family: "Poppins", weight: 600, size: 12, lineHeight: 2 },
          stepSize: 1,
          padding: 8,
          callback: function (value, index, values) {
            if (index === 0 || index === values.length - 1) return null;
            return value + "    ";
          },
        },
        min: 0,
        max: 4.5,
        grid: { display: false },
        border: { display: false },
      },
    },
  };

  return (
    <div style={{ height: "100%", width: "100%", position: "relative" }}>
      <Line
        ref={chartRef}
        data={chartData}
        options={{ ...defaultOptions, ...options }}
      />
    </div>
  );
};

export default LineChart;
